import { defineMessages, MessageDescriptor } from "react-intl";
import { ExtraCategory } from "../domain/ExtraCategory.enum";
import { ExtraRateType, ExtraUnitType } from "../domain/entities/CategoryExtraLine";
import { VehicleTypeCode } from "../domain/VehicleType";

type AssignableCategory = ExtraCategory.VEHICLE | ExtraCategory.FACILITIES;

/** Category names, translated here from the code (the backend never sends them). Dropdown values. */
export const categoryLabelMessages: Record<ExtraCategory, MessageDescriptor> = defineMessages({
  NONE: {
    id: "extrasCategories.category.NONE",
    defaultMessage: "No category",
  },
  VEHICLE: {
    id: "extrasCategories.category.VEHICLE",
    defaultMessage: "Vehicle",
  },
  FACILITIES: {
    id: "extrasCategories.category.FACILITIES",
    defaultMessage: "Facilities",
  },
});

/** Singular, in a sentence: "the first vehicle free of charge". */
export const categorySingularMessages: Record<
  AssignableCategory,
  MessageDescriptor
> = defineMessages({
  VEHICLE: {
    id: "extrasCategories.category.singular.VEHICLE",
    defaultMessage: "vehicle",
  },
  FACILITIES: {
    id: "extrasCategories.category.singular.FACILITIES",
    defaultMessage: "facility",
  },
});

/** Plural, in a sentence: "Limit vehicles in this accommodation". */
export const categoryPluralMessages: Record<AssignableCategory, MessageDescriptor> = defineMessages(
  {
    VEHICLE: {
      id: "extrasCategories.category.plural.VEHICLE",
      defaultMessage: "vehicles",
    },
    FACILITIES: {
      id: "extrasCategories.category.plural.FACILITIES",
      defaultMessage: "facilities",
    },
  }
);

/** Display names of the OTA vehicle types. The OTA code is never translated. */
export const vehicleTypeMessages: Record<VehicleTypeCode, MessageDescriptor> = defineMessages({
  "2WheelVehicle": {
    id: "extrasCategories.vehicleType.2WheelVehicle",
    defaultMessage: "Two-wheel vehicle",
  },
  "All-Terrain": {
    id: "extrasCategories.vehicleType.All-Terrain",
    defaultMessage: "All-terrain",
  },
  Boat: {
    id: "extrasCategories.vehicleType.Boat",
    defaultMessage: "Boat",
  },
  "Car/Sedan": {
    id: "extrasCategories.vehicleType.Car/Sedan",
    defaultMessage: "Car / Sedan",
  },
  "Coach/Bus": {
    id: "extrasCategories.vehicleType.Coach/Bus",
    defaultMessage: "Coach / Bus",
  },
  "CommercialVan/Truck": {
    id: "extrasCategories.vehicleType.CommercialVan/Truck",
    defaultMessage: "Commercial van / Truck",
  },
  Convertible: {
    id: "extrasCategories.vehicleType.Convertible",
    defaultMessage: "Convertible",
  },
  Coupe: {
    id: "extrasCategories.vehicleType.Coupe",
    defaultMessage: "Coupe",
  },
  Crossover: {
    id: "extrasCategories.vehicleType.Crossover",
    defaultMessage: "Crossover",
  },
  ExtendedCabPickup: {
    id: "extrasCategories.vehicleType.ExtendedCabPickup",
    defaultMessage: "Extended cab pickup",
  },
  Limo: {
    id: "extrasCategories.vehicleType.Limo",
    defaultMessage: "Limousine",
  },
  Monospace: {
    id: "extrasCategories.vehicleType.Monospace",
    defaultMessage: "Minivan",
  },
  Motorcycle: {
    id: "extrasCategories.vehicleType.Motorcycle",
    defaultMessage: "Motorcycle",
  },
  Motorhome: {
    id: "extrasCategories.vehicleType.Motorhome",
    defaultMessage: "Motorhome",
  },
  Pickup: {
    id: "extrasCategories.vehicleType.Pickup",
    defaultMessage: "Pickup",
  },
  Recreational: {
    id: "extrasCategories.vehicleType.Recreational",
    defaultMessage: "Recreational vehicle",
  },
  RegularCabPickup: {
    id: "extrasCategories.vehicleType.RegularCabPickup",
    defaultMessage: "Regular cab pickup",
  },
  Roadster: {
    id: "extrasCategories.vehicleType.Roadster",
    defaultMessage: "Roadster",
  },
  Shuttle: {
    id: "extrasCategories.vehicleType.Shuttle",
    defaultMessage: "Shuttle",
  },
  Special: {
    id: "extrasCategories.vehicleType.Special",
    defaultMessage: "Special",
  },
  SpecialOffer: {
    id: "extrasCategories.vehicleType.SpecialOffer",
    defaultMessage: "Special offer",
  },
  Sport: {
    id: "extrasCategories.vehicleType.Sport",
    defaultMessage: "Sport",
  },
  StationWagon: {
    id: "extrasCategories.vehicleType.StationWagon",
    defaultMessage: "Station wagon",
  },
  SUV: {
    id: "extrasCategories.vehicleType.SUV",
    defaultMessage: "SUV",
  },
  Train: {
    id: "extrasCategories.vehicleType.Train",
    defaultMessage: "Train",
  },
  Truck: {
    id: "extrasCategories.vehicleType.Truck",
    defaultMessage: "Truck",
  },
  Van: {
    id: "extrasCategories.vehicleType.Van",
    defaultMessage: "Van",
  },
});

/** Unit type of a line in the *Limits per category* list. */
export const unitTypeMessages: Record<
  Exclude<ExtraUnitType, ExtraUnitType.UNSPECIFIED>,
  MessageDescriptor
> = defineMessages({
  PER_STAY: {
    id: "extrasCategories.unitType.PER_STAY",
    defaultMessage: "Per unit · per stay",
  },
  PER_NIGHT: {
    id: "extrasCategories.unitType.PER_NIGHT",
    defaultMessage: "Per unit · per night",
  },
});

/** Shown instead of an amount when the price is not constant. */
export const rateTypeMessages: Record<
  Exclude<ExtraRateType, ExtraRateType.UNSPECIFIED>,
  MessageDescriptor
> = defineMessages({
  PERCENTAGE: {
    id: "extrasCategories.rateType.PERCENTAGE",
    defaultMessage: "Percentage",
  },
  FORMULA: {
    id: "extrasCategories.rateType.FORMULA",
    defaultMessage: "Formula",
  },
  OWN: {
    id: "extrasCategories.rateType.OWN",
    defaultMessage: "Own",
  },
  SHARED: {
    id: "extrasCategories.rateType.SHARED",
    defaultMessage: "Shared",
  },
});
